import { FC, useEffect, useRef, useState } from "react";

import { Devices, Facilities, Target, TargetStatus } from "@app/shared";
import { useParams } from "react-router-dom";
import ShakaPlayer from "shaka-player-react";

import { useFetch } from "../../../hooks";
import { useLiveViewHub } from "../../../hooks/useLiveViewHub";
import { LayoutDesigner } from "../../shared/LayoutDesigner/LayoutDesigner";
import { SvgTargetHistory } from "../../shared/LayoutDesigner/SvgTargetHistory";

export const DeviceDemo: FC = () => {
  const { id } = useParams();

  const [recentTargets, setRecentTargets] = useState<Target[]>([]);

  const [device] = useFetch(() => Devices.getById(id));
  // Both wait for the device: fetching with an undefined id sent two requests for "/undefined"
  // on every load.
  const [facility] = useFetch(() => (device?.facilityId ? Facilities.getById(device.facilityId) : Promise.resolve(undefined)), [device]);
  const [streamUrl] = useFetch(() => (device?.id ? Devices.getDeviceStream(device.id) : Promise.resolve(undefined)), [device]);

  /*
   * The stream is loaded by hand rather than through the player's `src` prop: the wrapper calls
   * player.load() without catching, so an offline unit (the stream URL exists whether or not the
   * device is answering) surfaced as an uncaught rejection instead of a message. shaka's error
   * codes are numeric; 1002 is the HTTP failure an unreachable stream produces.
   */
  const playerRef = useRef<{ player?: { load: (url: string) => Promise<void> } }>(null);
  const [streamError, setStreamError] = useState<string>();
  useEffect(() => {
    const player = playerRef.current?.player;
    if (!player || !streamUrl) return;
    let cancelled = false;
    setStreamError(undefined);
    player.load(streamUrl).catch((err: unknown) => {
      if (cancelled) return;
      const code = (err as { code?: number })?.code;
      setStreamError(code === 1002 ? "The device is not streaming right now." : "The video stream could not be loaded.");
    });
    return () => {
      cancelled = true;
    };
  }, [streamUrl]);

  useLiveViewHub(facility?.id, (targets: Target[]) => {
    const interestingTargets = recentTargets.filter(t => t.status === TargetStatus.Moving || t.status === TargetStatus.New);
    const newTargets = [...targets, ...interestingTargets.filter(t => !targets.find(nt => nt.id === t.id))];

    setRecentTargets(newTargets);
  });

  return (
    <div className="min-h-full h-full flex flex-col gap-6 p-4 md:flex-row md:p-6">
      <div className="flex-1 my-auto">
        {facility?.backgroundImageSource &&
          <LayoutDesigner facility={facility}>
            {recentTargets.map(t => (
              <SvgTargetHistory hideHistory key={t.id} color={device?.color} target={t} facility={facility} showTargets={false} />
            ))}
          </LayoutDesigner>
        }
      </div>
      <div className="flex-1 my-auto">
        {streamUrl &&
          <div className="bg-background-left-nav">
            <ShakaPlayer ref={playerRef} autoPlay />
            {streamError && <p className="p-3 text-sm text-white/80">{streamError}</p>}
          </div>
        }
      </div>
    </div>
  );
}